// Copyright (c) Docugami, Inc. All rights reserved.

import { useEventListener } from "hooks/EventListener";
import { FileManagerDataApi } from "networking/api";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { FileUploadCounts, StorageManagerInstance } from "storage/StorageManager";
import { AppState } from "store";
import { IEvent, Event } from "utils/Event";
import { DgDirectory, DgFile, FileManagerInstance } from "./FileManager";
import { FileUploaderInstance, FileUploadEvent, FileUploadStatus } from "./FileUploader";

/**
 * View of the total user file count
 */
export class FileCountView {
    private _count: number = -1;
    private _initialized: boolean = false;
    private _countChangedEvent: Event<number> = new Event();

    get count(): number | null { return this._initialized ? this._count : null; }
    get onCountChanged(): IEvent<number> { return this._countChangedEvent; }

    constructor() {
        FileManagerInstance.onItemRemoved.addListener(this.onItemRemoved.bind(this));
        FileUploaderInstance.onFileUpload.addListener(this.onItemAdded.bind(this))
        StorageManagerInstance.onFileUpload.addListener(this.onProviderItemAdded.bind(this));
    }

    async getCount(path?: string): Promise<number> {
        if (!this._initialized) {
            const fileManagerApi = new FileManagerDataApi();
            const response = await fileManagerApi.fileManagerDataGetFileCount(path);

            this._initialized = true;
            this.setCount(response.data);
        }

        return this._count;
    }

    private onItemAdded(event: FileUploadEvent) {
        if (!this._initialized) {
            return;
        }

        if (event.status === FileUploadStatus.Succeeded) {
            this.setCount(this._count + 1);
        }
    }

    private onProviderItemAdded(itemCount: FileUploadCounts) {
        if (!this._initialized) {
            return;
        }
        if (!itemCount.failedCount) {
            this.setCount(this._count + itemCount.totalCount);
        } else {
            this.setCount(this._count - itemCount.failedCount);
        }
    }

    private onItemRemoved(item: DgFile | DgDirectory) {
        if (!this._initialized) {
            return;
        }

        if (item instanceof DgFile) {
            this.setCount(this._count - 1);
        }
    }

    private setCount(count: number) {
        if (this._count !== count) {
            this._count = count;
            this._countChangedEvent.raise(this._count);
        }
    }
}

export const fileCountView: FileCountView = new FileCountView();

export function useFileCount(): number | null {
    const orgId = useSelector((state: AppState) => state.organizations.currentOrganization?.id);
    const fileCountViewRef = useRef<FileCountView>(fileCountView);
    const [numDocs, setNumDocs] = useState(fileCountViewRef.current.count);

    useEventListener(fileCountViewRef.current.onCountChanged, count => setNumDocs(count));

    useEffect(() => {
        if (!!orgId) {
            fileCountViewRef.current.getCount();
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgId]);

    return numDocs;
}
